import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Stamp, FileText, Truck, MapPin, Phone, User as UserIcon, Send,
  CheckCircle2, Clock, Package, Sparkles, AlertCircle, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

// Foto del materiale che il socio iscritto al Registro riceve
const MATERIALE_PHOTOS = [
  {
    titolo: "Timbro Rotondo Ufficiale A.C.D.T.",
    descrizione: "Timbro professionale in resina con dicitura ufficiale, codice tessera e ruolo. Lo usi per firmare documenti, contratti e attestati come consulente accreditato.",
    img: "https://images.unsplash.com/photo-1633613286848-e6f43bbafb8d?w=800&q=80",
    icon: Stamp,
  },
  {
    titolo: "Carta Intestata Istituzionale",
    descrizione: "100 fogli A4 stampati con il logo A.C.D.T., aggregazione MIMIT e tuoi riferimenti professionali. Per relazioni, perizie e comunicazioni ufficiali.",
    img: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&q=80",
    icon: FileText,
  },
  {
    titolo: "Sigillo Ufficiale Registro Professionisti Digitali",
    descrizione: "Sigillo a rilievo del Registro A.C.D.T. - Centro Studi E.S.S.E. ai sensi della L. 4/2013 (Agg. MIMIT). Da apporre su documenti, attestati e materiali ufficiali.",
    img: "/registro-logo.jpeg",
    icon: Stamp,
    isLogo: true,
  },
];

const STATO_BADGE = {
  richiesta: { label: "In attesa di spedizione", color: "bg-[#f59e0b]/15 text-[#b45309]", icon: Clock },
  spedita: { label: "Spedita", color: "bg-blue-100 text-blue-700", icon: Truck },
  consegnata: { label: "Consegnata", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

export default function MaterialeProTab({ user }) {
  const [spedizione, setSpedizione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    nome_destinatario: "", via: "", civico: "", citta: "", cap: "", provincia: "", telefono: "", note: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.iscrittoRegistro) { setLoading(false); return; }
    axios.get(`${API}/api/me/spedizione-materiale`, { withCredentials: true })
      .then((r) => {
        setSpedizione(r.data);
        if (r.data) {
          setForm({
            nome_destinatario: r.data.nome_destinatario || `${user.nome} ${user.cognome}`,
            via: r.data.via || "",
            civico: r.data.civico || "",
            citta: r.data.citta || user.citta || "",
            cap: r.data.cap || "",
            provincia: r.data.provincia || user.provincia || "",
            telefono: r.data.telefono || user.telefono || "",
            note: r.data.note || "",
          });
        } else {
          setForm((f) => ({
            ...f,
            nome_destinatario: `${user.nome} ${user.cognome}`,
            citta: user.citta || "",
            provincia: user.provincia || "",
            telefono: user.telefono || "",
          }));
          setEditing(true);
        }
      })
      .catch(() => setSpedizione(null))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user?.iscrittoRegistro) {
    return (
      <div data-testid="materiale-pro-locked" className="rounded-2xl border-2 border-dashed border-[#f59e0b]/40 bg-gradient-to-br from-[#fff7ed] to-white p-10 md:p-14 text-center max-w-2xl mx-auto">
        <div className="h-16 w-16 mx-auto rounded-2xl bg-[#f59e0b]/15 flex items-center justify-center mb-4">
          <Stamp className="h-8 w-8 text-[#b45309]" />
        </div>
        <h2 className="text-2xl font-extrabold text-[#0a1f55]">Materiale Professionale</h2>
        <p className="mt-3 text-[14px] text-slate-600 max-w-md mx-auto">
          Questa sezione è riservata ai soci <strong>iscritti al Registro Nazionale</strong>.
          Una volta confermata l&apos;iscrizione, potrai richiedere la spedizione del tuo
          <strong> timbro rotondo ufficiale</strong> e della <strong>carta intestata istituzionale</strong>.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[11.5px] font-extrabold">
          <Sparkles className="h-3.5 w-3.5" /> Sblocca iscrivendoti al Registro
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" />
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/me/spedizione-materiale`,
        { ...form, provincia: (form.provincia || "").toUpperCase().slice(0, 4) },
        { withCredentials: true }
      );
      setSpedizione(data);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante l'invio della richiesta");
    } finally {
      setSaving(false);
    }
  };

  const badge = spedizione ? STATO_BADGE[spedizione.stato] : null;
  const StatoIcon = badge?.icon;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[11px] font-extrabold uppercase tracking-[0.2em]">
          <Sparkles className="h-3.5 w-3.5" /> Solo per iscritti al Registro
        </div>
        <h2 data-testid="materiale-pro-title" className="mt-2 text-3xl font-extrabold text-[#0a1f55]">Materiale Professionale</h2>
        <p className="mt-1 text-[14px] text-slate-600">
          Ricevi il <strong>timbro rotondo</strong>, la <strong>carta intestata</strong> e il <strong>sigillo del Registro Professionisti Digitali</strong> ufficiali per operare come consulente accreditato A.C.D.T.
        </p>
      </div>

      {/* Foto del materiale */}
      <div data-testid="materiale-fotos" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {MATERIALE_PHOTOS.map((m) => {
          const I = m.icon;
          return (
            <div key={m.titolo} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-shadow">
              <div className={`aspect-[16/10] overflow-hidden ${m.isLogo ? "bg-gradient-to-br from-slate-50 to-white p-4 flex items-center justify-center" : "bg-slate-100"}`}>
                <img
                  src={m.img}
                  alt={m.titolo}
                  className={m.isLogo ? "max-h-full max-w-full object-contain" : "w-full h-full object-cover"}
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-9 w-9 rounded-lg text-white flex items-center justify-center ${m.isLogo ? "bg-[#f59e0b]" : "bg-[#102a6b]"}`}>
                    <I className="h-4 w-4" />
                  </div>
                  <h3 className="font-extrabold text-[#0a1f55] text-[15px]">{m.titolo}</h3>
                </div>
                <p className="text-[13px] text-slate-600 leading-snug">{m.descrizione}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stato spedizione */}
      {spedizione && !editing && (
        <div data-testid="spedizione-status" className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-extrabold ${badge.color}`}>
                <StatoIcon className="h-4 w-4" />
                {badge.label}
              </div>
              <h3 className="mt-3 text-xl font-extrabold text-[#0a1f55]">Spedizione richiesta il {new Date(spedizione.created_at).toLocaleDateString("it-IT")}</h3>
              <div className="mt-3 text-[13px] text-slate-700 space-y-0.5">
                <div><strong>{spedizione.nome_destinatario}</strong></div>
                <div>{spedizione.via} {spedizione.civico}</div>
                <div>{spedizione.cap} {spedizione.citta} ({spedizione.provincia})</div>
                <div className="text-slate-500">Tel: {spedizione.telefono}</div>
                {spedizione.note && <div className="text-[12px] text-slate-500 mt-1">Note: {spedizione.note}</div>}
              </div>
              {spedizione.tracking && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-[12.5px]">
                  <Package className="h-4 w-4" />
                  <span><strong>Tracking:</strong> {spedizione.tracking}</span>
                  {spedizione.corriere && <span className="text-blue-700">({spedizione.corriere})</span>}
                </div>
              )}
              {spedizione.note_admin && (
                <div className="mt-3 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <strong>Messaggio dalla segreteria:</strong> {spedizione.note_admin}
                </div>
              )}
            </div>
            {spedizione.stato !== "consegnata" && (
              <Button
                data-testid="btn-modifica-spedizione"
                variant="outline"
                onClick={() => setEditing(true)}
                className="border-[#102a6b]/30 text-[#102a6b] hover:bg-[#102a6b]/5 font-bold"
              >
                Modifica indirizzo
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Form */}
      {editing && (
        <form onSubmit={handleSubmit} data-testid="form-spedizione" className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h3 className="text-xl font-extrabold text-[#0a1f55]">
              {spedizione ? "Aggiorna indirizzo di spedizione" : "Dati di spedizione"}
            </h3>
            <p className="text-[13px] text-slate-500 mt-1">
              Inserisci l&apos;indirizzo dove vuoi ricevere il materiale. La segreteria evaderà la spedizione entro 7-10 giorni lavorativi.
            </p>
          </div>

          <Field icon={UserIcon} label="Nome destinatario" required value={form.nome_destinatario} onChange={onChange("nome_destinatario")} testid="sped-nome" placeholder="Es. Mario Rossi" />

          <div className="grid sm:grid-cols-[1fr_140px] gap-3">
            <Field icon={MapPin} label="Via / Indirizzo" required value={form.via} onChange={onChange("via")} testid="sped-via" placeholder="Via Roma" />
            <Field label="Civico" required value={form.civico} onChange={onChange("civico")} testid="sped-civico" placeholder="12/A" />
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="CAP" required value={form.cap} onChange={onChange("cap")} testid="sped-cap" placeholder="00100" />
            <Field label="Città" required value={form.citta} onChange={onChange("citta")} testid="sped-citta" placeholder="Roma" />
            <Field label="Provincia" required value={form.provincia} onChange={onChange("provincia")} testid="sped-provincia" placeholder="RM" maxLength={4} />
          </div>

          <Field icon={Phone} label="Telefono" required value={form.telefono} onChange={onChange("telefono")} testid="sped-telefono" placeholder="+39 333 1234567" />

          <div>
            <Label className="text-[13px] font-semibold text-[#0a1f55]">Note per la segreteria (opzionale)</Label>
            <Textarea
              data-testid="sped-note"
              value={form.note}
              onChange={onChange("note")}
              maxLength={300}
              rows={3}
              placeholder="Indicazioni di consegna, citofono, orari preferiti…"
              className="mt-1.5"
            />
          </div>

          {error && (
            <div data-testid="sped-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px] font-semibold p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {spedizione && (
              <Button type="button" variant="outline" onClick={() => setEditing(false)} className="border-slate-300">
                Annulla
              </Button>
            )}
            <Button
              type="submit"
              data-testid="btn-invia-spedizione"
              disabled={saving}
              className="flex-1 h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> {spedizione ? "Aggiorna richiesta" : "Invia richiesta spedizione"}</>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, required, value, onChange, placeholder, testid, maxLength }) {
  return (
    <div>
      <Label className="text-[13px] font-semibold text-[#0a1f55]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative mt-1.5">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
        <Input
          data-testid={testid}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`h-11 ${Icon ? "pl-10" : ""}`}
        />
      </div>
    </div>
  );
}
